// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Did the money follow the need? x is how thinly covered a region was BEFORE
// most of this funding was spent; y is the share of the towers standing there
// today that the public helped pay for.
//
// The y-axis is deliberately a SHARE, not towers per 1,000 km². The thinnest-
// covered regions are also by far the largest, so any per-area rate there
// mostly measures their size and produces a spectacular, meaningless ratio.
// Capital cities are excluded: black-spot money was never aimed at them.

import type { TowerData, ScatterPoint } from '../types';
import { attr, esc, num, pct } from '../format';
import { gloss } from '../glossary';
import { viewHead, legend, onActivate } from './ui';
import { attachSvgZoom } from '../svgZoom';

const STATE_COLOR: Record<string, string> = {
  'New South Wales': '#2563eb',
  Victoria: '#7c3aed',
  Queensland: '#b91c1c',
  'Western Australia': '#c2620a',
  'South Australia': '#0f766e',
  Tasmania: '#15803d',
  'Northern Territory': '#a16207',
  'Australian Capital Territory': '#0369a1',
  'Other Territories': '#64748b',
};

export function mount(el: HTMLElement, data: TowerData): void {
  const pts = data.ignored.points;
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  let stateFilter = params.get('state') && params.get('state') !== 'Australia' ? params.get('state')! : null;

  const states = [...new Set(pts.map((p) => p.state))].sort();

  el.innerHTML = `
    ${viewHead({
      title: 'Did the money follow the need?',
      question: 'Did public towers go to the regions that had the least coverage to begin with?',
      blurb: `Every dot is a non-capital ${gloss('sa3', 'region')}. Left means it had thin tower coverage back in ${data.meta.years[0]}, before most of this money was spent. Up means a large share of the towers standing there today were ${gloss('cofunded', 'publicly co-funded')}. A well-targeted program puts dots in the top-left.`,
    })}
    <div class="ctl-row">
      <select data-state aria-label="State filter">
        <option value="">Every state and territory</option>
        ${states.map((s) => `<option value="${attr(s)}"${s === stateFilter ? ' selected' : ''}>${esc(s)}</option>`).join('')}
      </select>
      <p class="ctl-note">Capital-city regions are excluded — this program was never aimed at them.</p>
    </div>
    <div data-scatter></div>
    ${legend(states.map((s) => ({ color: STATE_COLOR[s] ?? '#64748b', label: s })))}
    <div data-table></div>
    <p class="src-note">Tower density is towers per 1,000 km² across all three carriers in ${data.meta.years[0]}. Vertical axis is co-funded towers as a share of all towers in the region today. Dot size is population (ABS ERP ${data.meta.erpYear}). Scroll or drag to zoom.</p>
  `;

  const host = el.querySelector('[data-scatter]') as HTMLElement;
  const tableHost = el.querySelector('[data-table]') as HTMLElement;

  function render(): void {
    const rows = stateFilter ? pts.filter((p) => p.state === stateFilter) : pts;
    host.innerHTML = rows.length ? scatter(data, rows) : '<div class="empty">No regions match.</div>';
    const svg = host.querySelector('svg') as SVGSVGElement | null;
    if (svg) attachSvgZoom(svg, { maxScale: 12 });
    host.querySelectorAll('[data-code]').forEach((d) => {
      d.setAttribute('tabindex', '0');
      onActivate(d, () => {
        location.hash = `view=region&sa3=${d.getAttribute('data-code')}`;
      });
    });
    renderTable(rows);
  }

  function renderTable(rows: ScatterPoint[]): void {
    const thin = [...rows].sort((a, b) => a.basePerArea - b.basePerArea).slice(0, 12);
    tableHost.innerHTML = `<section class="card">
      <div class="card-head">
        <h2>The thinnest-covered regions</h2>
        <p>Ranked by how few towers stood per 1,000 km² in ${data.meta.years[0]}. Click any row to open the region.</p>
      </div>
      <div class="table-scroll">
        <table class="data-table">
          <thead><tr>
            <th>Region</th><th>State</th><th class="n">Area (km²)</th><th class="n">People</th>
            <th class="n">Towers ${data.meta.years[0]}</th><th class="n">Towers now</th><th class="n">Co-funded</th><th class="n">Public share</th>
          </tr></thead>
          <tbody>${thin
            .map(
              (p) => `<tr data-code="${attr(p.code)}" tabindex="0">
                <td>${esc(p.name)}</td><td>${esc(p.state)}</td>
                <td class="n">${num(p.areaKm2)}</td><td class="n">${num(p.pop)}</td>
                <td class="n">${num(p.base2018)}</td><td class="n">${num(p.total)}</td>
                <td class="n">${num(p.coFunded)}</td>
                <td class="n ${p.fundedShare >= 0.2 ? 'good' : p.coFunded === 0 ? 'bad' : ''}">${pct(p.fundedShare, 0)}</td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>
      </div>
    </section>`;
    tableHost.querySelectorAll('[data-code]').forEach((r) => {
      const go = () => {
        location.hash = `view=region&sa3=${r.getAttribute('data-code')}`;
      };
      r.addEventListener('click', go);
      r.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') go();
      });
    });
  }

  (el.querySelector('[data-state]') as HTMLSelectElement).addEventListener('change', (e) => {
    const v = (e.target as HTMLSelectElement).value;
    stateFilter = v || null;
    render();
  });

  render();
}

function scatter(data: TowerData, rows: ScatterPoint[]): string {
  const W = 960;
  const H = 460;
  const P = { t: 26, b: 56, l: 62, r: 24 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;

  const minX = 0.02;
  const maxX = Math.max(...rows.map((p) => p.basePerArea), 1) * 1.2;
  const lx = Math.log10(minX);
  const hx = Math.log10(maxX);
  const x = (v: number) => P.l + ((Math.log10(Math.max(v, minX)) - lx) / (hx - lx)) * iw;
  const maxY = Math.max(0.35, ...rows.map((p) => p.fundedShare)) * 1.05;
  const y = (v: number) => P.t + ih - (v / maxY) * ih;

  const maxPop = Math.max(...rows.map((p) => p.pop), 1);
  const r = (pop: number) => 3 + Math.sqrt(pop / maxPop) * 12;

  const medX = data.ignored.medianBasePerArea;
  const cross = `<line x1="${x(medX).toFixed(1)}" x2="${x(medX).toFixed(1)}" y1="${P.t}" y2="${H - P.b}" stroke="var(--border-strong)" stroke-dasharray="5 4" />
    <text x="${(x(medX) - 8).toFixed(1)}" y="${P.t + 14}" class="ax" text-anchor="end">thinner coverage in ${data.meta.years[0]}</text>
    <text x="${(x(medX) + 8).toFixed(1)}" y="${P.t + 14}" class="ax">better covered</text>`;

  const dots = [...rows]
    .sort((a, b) => b.pop - a.pop)
    .map((p) => {
      const tip = [
        `${p.name}, ${p.state}`,
        `${num(p.areaKm2)} km² · ${num(p.pop)} residents`,
        `${num(p.base2018)} towers in ${data.meta.years[0]} → ${num(p.total)} now`,
        `${num(p.coFunded)} publicly co-funded (${pct(p.fundedShare)} of the region's towers)`,
        'Click to open this region',
      ].join('\n');
      return `<circle cx="${x(p.basePerArea).toFixed(1)}" cy="${y(p.fundedShare).toFixed(1)}" r="${r(p.pop).toFixed(1)}"
        fill="${STATE_COLOR[p.state] ?? '#64748b'}" fill-opacity="0.55" stroke="${STATE_COLOR[p.state] ?? '#64748b'}" stroke-width="1.1"
        class="scat" data-code="${attr(p.code)}" role="button" data-tip="${attr(tip)}"><title>${esc(p.name)}, ${esc(p.state)}</title></circle>`;
    })
    .join('');

  const yTicks = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8]
    .filter((t) => t <= maxY)
    .map(
      (t) =>
        `<line x1="${P.l}" x2="${W - P.r}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" stroke="var(--border-subtle)" />
         <text x="${P.l - 8}" y="${(y(t) + 4).toFixed(1)}" class="ax" text-anchor="end">${Math.round(t * 100)}%</text>`,
    )
    .join('');

  const xTicks = [0.05, 0.5, 5, 50, 500]
    .filter((t) => t >= minX && t <= maxX)
    .map(
      (t) =>
        `<text x="${x(t).toFixed(1)}" y="${H - P.b + 22}" class="ax" text-anchor="middle">${t < 1 ? t : num(t)}</text>`,
    )
    .join('');

  return `<div class="chart scatter-wrap">
    <svg viewBox="0 0 ${W} ${H}" class="scatter" role="img"
      aria-label="Regional tower density in ${data.meta.years[0]} against the share of towers that were publicly co-funded">
      ${yTicks}${cross}${dots}
      <line x1="${P.l}" x2="${W - P.r}" y1="${H - P.b}" y2="${H - P.b}" stroke="var(--border-default)" />
      ${xTicks}
      <text x="${W / 2}" y="${H - 8}" class="ax-title" text-anchor="middle">towers per 1,000 km² in ${data.meta.years[0]} →</text>
      <text x="14" y="${P.t + ih / 2}" class="ax-title" text-anchor="middle" transform="rotate(-90 14 ${P.t + ih / 2})">share publicly funded →</text>
    </svg>
  </div>`;
}
